import React, { useContext } from 'react';
import StateContext from 'common/contexts/state-context';
import TrackingService from 'common/services/Tracking/Tracking';
import SalonGallery from '../../../ui/SalonGallery';
import StatusPill from '../../../ui/StatusPill';
import GoodaysWidget from '../../../ui/GoodaysWidget';
import OpeningHoursGrid from '../../../ui/OpeningHoursGrid';
import { SearchIcon, PinIcon, PhoneIcon, FacebookIcon, InstagramIcon, StarOutlineIcon } from '../../../ui/Icons';
import { getOpeningStatus, getWeekSchedule, timeZoneForAddress } from '../../../services/openingStatus';
import { distanceLabel } from '../../../ui/format';
import { facebookUrl, instagramUrl, reservationUrl } from '../../../extendedFields';
import goToTab from '../../../services/tabNavigation';

/**
 * `.salon-hero` — galerie à gauche, panneau identité à droite.
 *
 * Décision 9 : la puce de distance dépend du point de référence
 * (`state.searchOrigin`, posé par la recherche précédente) — sans point de
 * référence, aucune puce n'est rendue plutôt que d'en inventer une.
 *
 * §18 du plan : le téléphone existe (`merchant.phone`) mais la maquette n'a
 * aucun `tel:` — corrigé ici, le CTA disparaît s'il n'y a pas de numéro.
 *
 * Les deux CTA sortants sont pistés (`phonecall`, `direction`) : ce sont les
 * seules conversions d'une fiche salon, et c'est sur ces deux gestes précis que
 * `common` les attend (`components/StoreCard.js:43,51`). L'itinéraire les avait
 * perdus en chemin — `templates/VOG/.../PlaceInfos.js:26` le pistait, plus
 * `2025_VOG/widgets/Itinary.js`. Aucun `preventDefault` n'est nécessaire :
 * `tel:` ne décharge pas le document et l'itinéraire ouvre un nouvel onglet, la
 * requête de tracking a donc le temps de partir.
 */
const SalonHero = ({ photos, goodaysRating, goodaysReviewCount, hasReviews }) => {
  const [state] = useContext(StateContext);
  const { merchant } = state;
  const origin = state.searchOrigin;
  // Les horaires se lisent dans le fuseau du salon, pas dans celui du visiteur
  // ni dans celui du conteneur SSR (cf. décision #16 du PLAN.md).
  const timeZone = timeZoneForAddress(merchant.address);
  const status = getOpeningStatus(merchant.workingTime, timeZone);
  const schedule = getWeekSchedule(merchant.workingTime, timeZone);
  const distance = distanceLabel(merchant.distance, origin);

  const trackCall = () => TrackingService.trackClickToCall(merchant.id, 'real');
  const trackDirection = () => TrackingService.trackClickToDirection(merchant.id, 'real');

  // Mapping et normalisation des URLs centralisés dans `src/extendedFields.js` —
  // la sémantique des champs étendus est une donnée de marque, pas du code.
  const facebook = facebookUrl(merchant);
  const instagram = instagramUrl(merchant);
  const reservation = reservationUrl(merchant);

  return (
    <section className="salon-hero" id="salon-hero">
      <div className="container salon-hero-grid">
        <div className="salon-hero-gallery-col">
          <SalonGallery photos={photos} salonName={merchant.name} />
        </div>

        <div className="salon-panel">
          <a href="/" className="salon-newsearch">
            <SearchIcon size={13} />
            Nouvelle recherche
          </a>

          <h1 itemProp="name">{merchant.name}</h1>

          {status.label && <StatusPill variant="panel" isOpen={status.isOpen} label={status.label} title={status.title} />}

          {/* Sans note Goodays, le conteneur ne laisse qu'une marge de 10px — moins
              visible que le cadre vide de l'onglet Avis, mais c'est le même trou. */}
          {goodaysRating ? (
            <div className="salon-panel-rating">
              <GoodaysWidget
                variant="compact"
                value={goodaysRating}
                reviewCount={goodaysReviewCount}
              />
            </div>
          ) : null}

          <div className="salon-panel-addr">
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ marginBottom: '6px' }} itemProp="address">
                {merchant.address.street}
                <br />
                {merchant.address.zip} {merchant.address.city}
              </p>
              {distance && <span className="dist">{distance}</span>}
            </div>
            <OpeningHoursGrid schedule={schedule} />
          </div>

          <div className="salon-panel-cta">
            {reservation && (
              <a href={reservation} target="_blank" rel="noopener noreferrer" className="btn">
                <PinIcon size={15} strokeWidth={2.5} />
                Prendre place dans la file
              </a>
            )}
            {merchant.phone && (
              <a href={`tel:${merchant.phone.replace(/\s/g, '')}`} className="btn yellow" onClick={trackCall}>
                <PhoneIcon size={15} />
                Appeler le salon
              </a>
            )}
          </div>

          <div className="salon-panel-links">
            {/* Itinéraire : la maquette ne faisait que changer d'onglet — la
                cible réelle est les directions Google Maps (§13 Phase 2). */}
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                merchant.formattedAddress,
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackDirection}
            >
              <PinIcon size={15} strokeWidth={2.2} />
              Itinéraire
            </a>
            <a
              href="#tab-tarifs"
              onClick={(event) => {
                event.preventDefault();
                goToTab('tarifs');
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  fontSize: '15px',
                  fontWeight: 800,
                  lineHeight: 1,
                  width: '15px',
                  textAlign: 'center',
                  flexShrink: 0,
                }}
              >
                €
              </span>
              Tarifs
            </a>
            {facebook && (
              <a href={facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <FacebookIcon size={15} />
                Facebook
              </a>
            )}
            {instagram && (
              <a href={instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <InstagramIcon size={15} />
                Instagram
              </a>
            )}
            {/* L'onglet Avis n'est monté que si le salon a des avis Goodays
                (`LocalPage`) : sans cette condition, le lien menait à un onglet
                absent et le clic ne faisait rien du tout. */}
            {hasReviews && (
              <a
                href="#tab-avis"
                onClick={(event) => {
                  event.preventDefault();
                  goToTab('avis');
                }}
              >
                <StarOutlineIcon size={15} />
                Nos avis
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SalonHero;
